import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EpisodeHero } from "@/components/EpisodeHero";
import { EpisodeCard } from "@/components/EpisodeCard";
import { Skeleton } from "@/components/ui/skeleton";

const ShowTile = ({ show, index }) => (
  <Link to={`/shows/${show.slug}`} className="group card-surface card-surface-hover rounded-2xl p-3 reveal" style={{ animationDelay: `${index * 40}ms` }} data-testid={`category-show-${show.slug}`}>
    <div className="aspect-square rounded-xl overflow-hidden">
      {show.cover_url ? <img src={show.cover_url} alt={show.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="h-full w-full bg-[#053671]" />}
    </div>
    <h3 className="mt-3 font-display font-bold leading-snug text-sm">{show.name}</h3>
    {show.host && <p className="text-xs text-slate-400 truncate">{show.host}</p>}
  </Link>
);

export default function CategoryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false);
    api.get(`/categories/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  const eps = data?.latest_episodes || [];

  return (
    <div className="min-h-screen flex flex-col" data-testid="category-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-32">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="category-back-link"><ArrowLeft size={16} /> Back to home</Link>
        {error ? (
          <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="category-not-found">Category not found.</div>
        ) : !data ? (
          <div className="space-y-6"><Skeleton className="h-24 rounded-2xl bg-white/5" /><Skeleton className="h-[380px] rounded-3xl bg-white/5" /></div>
        ) : (
          <>
            <p className="eyebrow mb-2">Category</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none" data-testid="category-title">{data.name}</h1>
            <p className="mt-3 text-slate-400" data-testid="category-show-count">{data.show_count} show{data.show_count === 1 ? "" : "s"}{data.subgroups.length ? ` across ${data.subgroups.length} sub-categories` : ""}</p>

            {eps.length > 0 && (
              <section className="mt-12" data-testid="category-latest-section">
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">Newest in {data.name}</h2>
                <EpisodeHero episode={eps[0]} eyebrow={`Newest in ${data.name}`} testId="category-hero" />
                {eps.length > 1 && (
                  <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="category-episodes-grid">
                    {eps.slice(1).map((ep, i) => <EpisodeCard key={ep.id} episode={ep} index={i + 1} />)}
                  </div>
                )}
              </section>
            )}

            <section className="mt-16" data-testid="category-shows-section">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">All {data.name} shows</h2>
              {data.show_count === 0 && <p className="text-slate-400" data-testid="category-empty">New shows coming soon.</p>}
              {data.subgroups.map((sg) => (
                <div key={sg.name} className="mb-10" data-testid={`category-subgroup-${sg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center gap-4 mb-4"><p className="eyebrow">{sg.name}</p><span className="h-px flex-1 bg-white/10" /><span className="font-mono text-xs text-slate-500">{sg.shows.length}</span></div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">{sg.shows.map((s, i) => <ShowTile key={s.id} show={s} index={i} />)}</div>
                </div>
              ))}
              {data.shows.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">{data.shows.map((s, i) => <ShowTile key={s.id} show={s} index={i} />)}</div>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
